import { IconComponent } from '@vgw/multibrand-fe-icon'
import { Link } from '@vgw/multibrand-fe-link'
import { FooterNavigation } from './footer-navigation'
import { CookieConsentBanner } from './cookie-consent-banner/cookie-consent-banner'
import { Trans } from '@lingui/react/macro'
import { Disclaimer } from './disclaimer'
import { LicenseDetails } from './license-details'
import { FOOTER_LOGOS, SOCIAL_MEDIA_LOGOS } from './config'
import { useAmoeLaunch } from '@/features/amoe/use-amoe-launch'
import { getBrandConfig } from '@/config/config'
import { MessageDescriptor } from '@lingui/core'
import { useState } from 'react'

type FooterNavIcon = {
  icon: IconComponent
  url: string
  title: MessageDescriptor
  className?: string
}

type FooterNavProps = {
  rsgIcons: readonly FooterNavIcon[]
  socialMediaIcons: readonly FooterNavIcon[]
}

export type FooterProps = {
  suppressAmoe?: boolean
}
export const Footer = (props: FooterProps) => {
  const [isCookiesBannerVisible, setIsCookiesBannerVisible] = useState(false)
  const { open: openAMOEDialog } = useAmoeLaunch()

  return (
    <footer className="self-stretch bg-surface px-2 py-10 pb-20 sm:px-4 lg:px-6 lg:pb-10">
      <div className="mx-auto flex w-full max-w-(--breakpoint-2xl) flex-col gap-6 text-xs text-content">
        <FooterNavigation
          suppressAmoe={!!props.suppressAmoe}
          onCookiesConsentOpen={() => setIsCookiesBannerVisible(true)}
          onAMOEDialogOpen={openAMOEDialog}
        />
        <NavIcons
          rsgIcons={FOOTER_LOGOS}
          socialMediaIcons={SOCIAL_MEDIA_LOGOS}
        />
        <Disclaimer />
        <LicenseDetails />
      </div>
      {isCookiesBannerVisible ? (
        <CookieConsentBanner
          onCookiesBannerHide={() => setIsCookiesBannerVisible(false)}
        />
      ) : null}
    </footer>
  )
}

const NavIcons = ({ rsgIcons, socialMediaIcons }: FooterNavProps) => {
  const { pamLegalDocumentsUrl } = getBrandConfig()

  return (
    <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:gap-0 lg:m-auto lg:gap-16">
      <div
        className="flex flex-wrap items-center justify-center gap-4 md:flex-nowrap md:gap-2.5 lg:justify-between lg:gap-4"
        data-testid="footer-logos"
      >
        {createIconsGroup(rsgIcons)}
        <Link
          key="rsg-link"
          size="xs"
          target="_blank"
          rel="noopener"
          href={`${pamLegalDocumentsUrl}/responsible-social-gameplay-policy`}
          className="px-1.5 py-1"
          weight="medium"
        >
          <Trans>
            Gameplay can be harmful if not kept in control.
            <br />
            Please play responsibly.
          </Trans>
        </Link>
      </div>
      <div className="flex gap-4 md:gap-2.5">
        {createIconsGroup(socialMediaIcons)}
      </div>
    </div>
  )
}

const createIconsGroup = (icons: readonly FooterNavIcon[]) => {
  return icons.map(({ icon: Icon, url, title, className }, i) => {
    return (
      <Link
        key={i}
        href={url}
        target="_blank"
        rel="noopener"
        title={title.message}
      >
        <Icon className={className} aria-hidden />
      </Link>
    )
  })
}
